"use client";

import React from "react";
import { ArrowRight, BookOpen, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function IntelligenceGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="flex h-[450px] animate-pulse flex-col rounded-none border-l-4 border-l-muted bg-card/50">
            <div className="h-48 w-full bg-muted/20" />
            <CardHeader>
              <div className="h-6 w-3/4 bg-muted/30" />
              <div className="h-6 w-1/2 bg-muted/30" />
            </CardHeader>
            <CardContent className="flex-grow">
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted/20" />
                <div className="h-4 w-full bg-muted/20" />
                <div className="h-4 w-2/3 bg-muted/20" />
              </div>
            </CardContent>
            <CardFooter>
              <div className="h-10 w-full bg-muted/30" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-none border border-destructive/50 bg-destructive/10 p-12 text-center">
        <TriangleAlert className="mb-4 size-12 text-destructive" />
        <h3 className="mb-2 text-xl font-bold text-foreground">
          Intelligence Feed Offline
        </h3>
        <p className="text-muted-foreground">
          Unable to retrieve the latest field reports. Please check connection.
        </p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-none border border-border bg-card/50 p-12 text-center">
        <BookOpen className="mb-4 size-12 text-muted-foreground" />
        <h3 className="mb-2 text-xl font-bold text-foreground">
          No Intelligence Available
        </h3>
        <p className="text-muted-foreground">
          Field reports are currently being compiled. Check back soon.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden rounded-none border-y-0 border-r-0 border-l-4 border-l-primary bg-card transition-colors hover:bg-accent/10">
          {post.featuredImage ? <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
              <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0 md:grayscale" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-60" />
            </div> : <div className="flex aspect-[16/9] w-full items-center justify-center bg-muted/30">
              <BookOpen className="size-12 text-muted-foreground/30" />
            </div>}
          <CardHeader className="pt-6">
            <CardTitle className="font-sans text-xl font-black uppercase leading-tight tracking-tight text-foreground">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow">
            <p className="line-clamp-3 text-sm text-muted-foreground">
              {post.excerpt ? post.excerpt.replace(/<[^>]*>?/gm, "") : "Read the full field report for detailed insights and engineering specifications."}
            </p>
          </CardContent>
          <CardFooter className="pb-6">
            <Button asChild variant="outline" className="w-full rounded-none border-primary/50 text-primary transition-all hover:bg-primary hover:text-primary-foreground">
              <Link to={post.link || "#"}>
                <span className="font-bold uppercase tracking-wider">
                  Read Intelligence
                </span>
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function FieldIntelligence() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="field-intelligence" className="dark bg-background py-24 text-foreground">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-16 flex flex-col items-start justify-between gap-6 border-b border-border pb-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-sans text-4xl font-black uppercase tracking-tighter text-foreground md:text-5xl lg:text-6xl">
              Field <span className="text-primary">Notes</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">Insights and best practices on site development, guidelines, and Northwoods excavating best practices.</p>
          </div>
          <Button asChild variant="ghost" className="hidden rounded-none text-muted-foreground hover:text-primary md:inline-flex">
            <Link to="/SiteEngineeringHubPage">
              <span className="font-bold uppercase tracking-wider">View All Notes</span>
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <IntelligenceGrid />
        </PostsProvider>

        <div className="mt-12 flex justify-center md:hidden">
          <Button asChild variant="outline" className="w-full rounded-none border-border text-foreground">
            <Link to="/SiteEngineeringHubPage">
              <span className="font-bold uppercase tracking-wider">
                View All Reports
              </span>
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}